import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { ClientLink, EmpresaLink } from '../components/client360/c360Shared';
import { useAuth } from '../contexts/AuthContext';
import { useRefreshListener } from '../hooks/useRefreshListener';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Lightbulb, Loader2, Filter, RefreshCw, ArrowRight, Calendar, Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { OpFollowCell, OP_ESTADOS } from '../components/OpFollowCell';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const EMPRESAS = [
  { key: 'edp', label: 'EDP' }, { key: 'galp', label: 'Galp' },
  { key: 'endesa', label: 'Endesa' }, { key: 'iberdrola', label: 'Iberdrola' },
  { key: 'gold', label: 'Gold' }, { key: 'repsol', label: 'Repsol' }, { key: 'yes', label: 'Yes' },
  { key: 'nos', label: 'NOS' }, { key: 'meo', label: 'MEO' }, { key: 'vodafone', label: 'Vodafone' },
  { key: 'nos_securitas', label: 'NOS Securitas' }, { key: 'prosegur', label: 'Prosegur' },
];
const empresaLabel = (k) => EMPRESAS.find(e => e.key === k)?.label || k;

const fmtDate = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleDateString('pt-PT'); } catch (e) { return iso; }
};

const StatusBadge = ({ status, dias }) => {
  if (status === 'liberto') return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-700">Já liberto ({Math.abs(dias)}d atrás)</span>;
  if (status === 'a_terminar') return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-700">Termina em {dias}d</span>;
  if (status === 'preso') return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-200 text-slate-700">Preso · {dias}d restantes</span>;
  return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-600">{status}</span>;
};

export const OportunidadesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [windowDays, setWindowDays] = useState('7');
  const [empresa, setEmpresa] = useState('all');
  const [onlyDue, setOnlyDue] = useState(true);
  const [comercial, setComercial] = useState('all');
  const [comerciais, setComerciais] = useState([]);
  const [tab, setTab] = useState('acompanhamento');
  const [estadoF, setEstadoF] = useState('all');
  const [counts, setCounts] = useState({});
  const isAdminLike = ['super_admin', 'gestor'].includes(user?.role);

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [windowDays, empresa, onlyDue, comercial, tab, estadoF]);
  useEffect(() => {
    if (!isAdminLike) return;
    axios.get(`${API_URL}/users`).then(r => {
      const list = (r.data || [])
        .filter(u => ['comercial', 'chefe_equipa', 'admin', 'parceiro', 'gestor', 'super_admin'].includes(u.role))
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt'));
      setComerciais(list);
    }).catch(() => {});
  }, [isAdminLike]);
  useRefreshListener(() => fetchData());

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = { window_days: parseInt(windowDays, 10), only_due: onlyDue, tab };
      if (tab === 'acompanhamento' && estadoF !== 'all') params.estado = estadoF;
      if (empresa !== 'all') params.empresa = empresa;
      if (isAdminLike && comercial !== 'all') params.comercial_id = comercial;
      const res = await axios.get(`${API_URL}/oportunidades`, { params });
      setItems(res.data.items || []);
      setCounts(res.data.counts || {});
    } catch (err) {
      toast.error('Erro ao carregar oportunidades');
    } finally {
      setLoading(false);
    }
  };

  const grouped = useMemo(() => {
    return {
      liberto: items.filter(i => i.status === 'liberto'),
      a_terminar: items.filter(i => i.status === 'a_terminar'),
      preso: items.filter(i => i.status === 'preso'),
    };
  }, [items]);

  const createLeadFromContract = (item) => {
    // Pre-fill new lead form via query params, navigate to LeadsPage
    const params = new URLSearchParams({
      from_oportunidade: '1',
      contract_id: item.id,
      nome: item.nome_cliente || '',
      nif: item.nif || '',
      empresa_atual: item.empresa || '',
    });
    navigate(`/leads?${params.toString()}`);
  };

  const triggerNotifications = async () => {
    try {
      const r = await axios.post(`${API_URL}/oportunidades/notify-due`);
      toast.success(`Enviadas ${r.data.notified || 0} notificações`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro');
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="oportunidades-page">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-outfit flex items-center gap-2">
              <Lightbulb className="w-6 h-6 text-amber-500" />
              Oportunidades de Troca
            </h1>
            <p className="text-slate-500 mt-1 text-sm">
              Contratos com fidelização a terminar ou já libertos — pode contactar o cliente para troca de companhia
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchData} className="gap-2" data-testid="op-refresh">
              <RefreshCw className="w-4 h-4" /> Atualizar
            </Button>
            {isAdminLike && (
              <Button onClick={triggerNotifications} className="bg-[#2D3E6F] hover:bg-[#1a2a4a] gap-2" data-testid="op-notify">
                <Lightbulb className="w-4 h-4" /> Disparar notificações
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2" data-testid="op-tabs">
          {[
            { key: 'acompanhamento', label: 'Em acompanhamento' },
            { key: 'sem_troca', label: 'Sem troca (mantém-se)' },
            { key: 'fechado', label: 'Contrato fechado' },
          ].map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${tab === t.key ? 'bg-[#2D3E6F] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-[#2D3E6F]'}`}
              data-testid={`op-tab-${t.key}`}
            >
              {t.label} <span className="ml-1 opacity-70" data-testid={`op-tab-count-${t.key}`}>({counts[t.key] ?? 0})</span>
            </button>
          ))}
        </div>

        {tab === 'sem_troca' && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-xl px-4 py-2" data-testid="op-sem-troca-info">
            Estes clientes voltam automaticamente para "Em acompanhamento" (Por contactar) de 2 em 2 meses, até haver troca. O comercial e o gestor recebem aviso.
          </p>
        )}

        {/* Stats */}
        <div className={`grid grid-cols-1 gap-4 ${onlyDue ? 'md:grid-cols-3' : 'md:grid-cols-4'}`}>
          <Card className="rounded-2xl border-slate-100">
            <CardContent className="p-5">
              <p className="text-xs uppercase tracking-wider text-slate-500">Total visíveis</p>
              <p className="text-3xl font-bold text-slate-900 mt-1">{items.length}</p>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-emerald-100">
            <CardContent className="p-5">
              <p className="text-xs uppercase tracking-wider text-emerald-700">Já libertos</p>
              <p className="text-3xl font-bold text-emerald-700 mt-1">{grouped.liberto.length}</p>
              <p className="text-xs text-slate-400 mt-1">Pode trocar agora</p>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-amber-100">
            <CardContent className="p-5">
              <p className="text-xs uppercase tracking-wider text-amber-700">A terminar (≤ {windowDays}d)</p>
              <p className="text-3xl font-bold text-amber-700 mt-1">{grouped.a_terminar.length}</p>
              <p className="text-xs text-slate-400 mt-1">Avisar com antecedência</p>
            </CardContent>
          </Card>
          {!onlyDue && (
            <Card className="rounded-2xl border-slate-200">
              <CardContent className="p-5">
                <p className="text-xs uppercase tracking-wider text-slate-600">Ainda presos</p>
                <p className="text-3xl font-bold text-slate-700 mt-1">{grouped.preso.length}</p>
                <p className="text-xs text-slate-400 mt-1">Fidelização ainda activa</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Filters */}
        <Card className="rounded-2xl border-slate-100">
          <CardContent className="p-4">
            <div className={`grid grid-cols-1 gap-3 ${isAdminLike ? 'md:grid-cols-5' : 'md:grid-cols-4'}`}>
              <div>
                <label className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Janela (dias)
                </label>
                <Select value={windowDays} onValueChange={setWindowDays}>
                  <SelectTrigger data-testid="op-filter-window"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">7 dias</SelectItem>
                    <SelectItem value="14">14 dias</SelectItem>
                    <SelectItem value="30">30 dias</SelectItem>
                    <SelectItem value="60">60 dias</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                  <Filter className="w-3 h-3" /> Empresa
                </label>
                <Select value={empresa} onValueChange={setEmpresa}>
                  <SelectTrigger data-testid="op-filter-empresa"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {EMPRESAS.map(e => <SelectItem key={e.key} value={e.key}>{e.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                  <Filter className="w-3 h-3" /> Estado de acompanhamento
                </label>
                <Select value={estadoF} onValueChange={setEstadoF} disabled={tab !== 'acompanhamento'}>
                  <SelectTrigger data-testid="op-filter-estado"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {OP_ESTADOS.slice(0, 4).map(e => <SelectItem key={e.key} value={e.key}>{e.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {isAdminLike && (
                <div>
                  <label className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                    <Users className="w-3 h-3" /> Comercial
                  </label>
                  <Select value={comercial} onValueChange={setComercial}>
                    <SelectTrigger data-testid="op-filter-comercial"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {comerciais.map(u => (
                        <SelectItem key={u.id} value={u.id} data-testid={`op-comercial-${u.id}`}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    checked={!onlyDue}
                    onChange={(e) => setOnlyDue(!e.target.checked)}
                    className="w-4 h-4 accent-[#2D3E6F]"
                    data-testid="op-show-all"
                  />
                  Mostrar também ainda presos
                </label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : items.length === 0 ? (
          <Card className="rounded-2xl border-slate-100">
            <CardContent className="text-center py-16">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">{tab === 'acompanhamento' ? 'Sem oportunidades nesta janela' : 'Sem clientes neste separador'}</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="rounded-2xl border-slate-100 overflow-hidden">
            <div className="overflow-x-auto">
              <Table className="w-full" style={{ tableLayout: 'auto' }}>
                <TableHeader>
                  <TableRow className="bg-slate-50/50">
                    <TableHead className="whitespace-nowrap" style={{ minWidth: 60 }}>Cód.</TableHead>
                    <TableHead className="whitespace-nowrap" style={{ minWidth: 170 }}>Cliente</TableHead>
                    <TableHead className="whitespace-nowrap" style={{ minWidth: 110 }}>NIF</TableHead>
                    <TableHead className="whitespace-nowrap" style={{ minWidth: 110 }}>Empresa Atual</TableHead>
                    <TableHead className="whitespace-nowrap" style={{ minWidth: 110 }}>Ativação</TableHead>
                    <TableHead className="whitespace-nowrap" style={{ minWidth: 130 }}>Fim Fidelização</TableHead>
                    <TableHead className="whitespace-nowrap" style={{ minWidth: 140 }}>Estado</TableHead>
                    <TableHead className="whitespace-nowrap" style={{ minWidth: 190 }}>Acompanhamento</TableHead>
                    <TableHead className="whitespace-nowrap" style={{ minWidth: 130 }}>Comercial</TableHead>
                    <TableHead className="whitespace-nowrap" style={{ minWidth: 140 }}></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it) => (
                    <TableRow key={it.id} className="hover:bg-slate-50/50" data-testid={`op-row-${it.id}`}>
                      <TableCell className="font-mono">{it.codigo}</TableCell>
                      <TableCell className="font-medium"><span className="inline-flex items-center gap-1">{it.nome_cliente}<ClientLink nif={it.nif} name={it.nome_cliente} contractId={it.id} testId={`op-client-link-${it.id}`} /></span></TableCell>
                      <TableCell className="font-mono text-sm">{it.nif}</TableCell>
                      <TableCell><EmpresaLink company={it.empresa} testId={`op-empresa-link-${it.id}`} /></TableCell>
                      <TableCell>{fmtDate(it.data_ativacao)}</TableCell>
                      <TableCell>{fmtDate(it.data_fim_fidelizacao)}</TableCell>
                      <TableCell><StatusBadge status={it.status} dias={it.dias_restantes} /></TableCell>
                      <TableCell><OpFollowCell item={it} onSaved={fetchData} /></TableCell>
                      <TableCell className="text-sm text-slate-600">{it.assigned_to_name || '-'}</TableCell>
                      <TableCell>
                        {tab === 'fechado' ? null : it.status === 'preso' ? (
                          <span className="text-xs text-slate-400">
                            Disponível em {it.dias_restantes}d
                          </span>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => createLeadFromContract(it)}
                            className="gap-1"
                            data-testid={`op-create-lead-${it.id}`}
                          >
                            Criar lead troca <ArrowRight className="w-3 h-3" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default OportunidadesPage;
